/** Browser preparation is optional; the server owns canonical storage. */
export const UPLOAD_MAX_DIMENSION = 2800;
export const UPLOAD_JPEG_QUALITY = 0.92;
/** Legacy API compatibility only; not a compression target. */
export const UPLOAD_MAX_BYTES = 8 * 1024 * 1024;
export const UPLOAD_MAX_DECODE_BYTES = 30 * 1024 * 1024;
export interface DownscaleResult {
  status: 'prepared' | 'unchanged' | 'serverRequired';
  file: File; width: number; height: number; reason: string;
  changed: boolean; originalBytes: number; bytes: number; budgetExceeded: boolean;
}
export async function downscaleImageFile(file: File, maxDimension = UPLOAD_MAX_DIMENSION,
  quality = UPLOAD_JPEG_QUALITY, logo = false): Promise<DownscaleResult> {
  const result = (status: DownscaleResult['status'], reason: string, width = 0, height = 0,
    output = file): DownscaleResult => ({ status, reason, width, height, file: output,
      changed: output !== file, originalBytes: file.size, bytes: output.size,
      budgetExceeded: output.size > (logo ? 100 : 750) * 1024 });
  if (file.size > UPLOAD_MAX_DECODE_BYTES) throw new Error('This photo exceeds the 30 MiB processing safeguard.');
  if (/\.(heic|heif)$/i.test(file.name) || /^image\/hei[cf]$/i.test(file.type)) return result('serverRequired', 'heic-format');
  let source: ImageBitmap | null = null;
  try {
    const dimensions = await inspectImageHeader(file);
    if (!dimensions || dimensions.animated || dimensions.width * dimensions.height > 16000000)
      return result('serverRequired', dimensions?.animated ? 'animation' : 'format-or-dimensions');
    // createImageBitmap normalizes EXIF orientation. Browsers without it use the
    // authenticated worker instead of guessing at a canvas coordinate basis.
    if (typeof createImageBitmap !== 'function') return result('serverRequired', 'browser-decoder-unavailable');
    source = await createImageBitmap(file, { imageOrientation: 'from-image' });
    if (!source.width || !source.height) return result('serverRequired', 'unknown-dimensions');
    const edge = logo ? 1024 : maxDimension;
    const scale = Math.min(1, edge / Math.max(source.width, source.height));
    if (scale === 1 && file.type === 'image/jpeg' && file.size <= 750 * 1024 && !dimensions.metadata)
      return result('unchanged', 'already-compliant-photo', source.width, source.height);
    const width = Math.max(1, Math.floor(source.width * scale));
    const height = Math.max(1, Math.floor(source.height * scale));
    const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return result('serverRequired', 'canvas-unavailable');
    context.imageSmoothingEnabled = true; context.imageSmoothingQuality = 'high';
    context.drawImage(source, 0, 0, width, height);
    const pixels = context.getImageData(0, 0, width, height).data;
    let transparent = false;
    for (let i = 3; i < pixels.length; i += 4) if (pixels[i] < 255) { transparent = true; break; }
    const type = logo || transparent ? 'image/png' : 'image/jpeg';
    let blob: Blob | null = null;
    for (const q of type === 'image/png' ? [1] : [quality, 0.90, 0.88]) {
      blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, q));
      if (!blob) return result('serverRequired', 'browser-encoder-failed');
      if (blob.size <= (logo ? 100 : 750) * 1024) break;
    }
    if (!blob || blob.type !== type) return result('serverRequired', 'browser-encoder-format');
    // Do not return the camera file on an encoder failure or a byte-budget miss.
    // The server may copy an already canonical master after inspecting metadata.
    const output = new File([blob], file.name.replace(/\.[^.]+$/, '') + (type === 'image/png' ? '.png' : '.jpg'), { type });
    return result('prepared', 'normalized-browser-master', width, height, output);
  } catch {
    return result('serverRequired', 'browser-preparation-failed');
  } finally { source?.close(); }
}
/** Header-only preflight, before any full pixel allocation. */
export async function inspectImageHeader(file: Blob): Promise<{ width: number; height: number; animated?: boolean; metadata?: boolean } | null> {
  const bytes = new Uint8Array(await file.slice(0, 256 * 1024).arrayBuffer());
  const view = new DataView(bytes.buffer);
  const ascii = (offset: number, length: number) => String.fromCharCode(...bytes.slice(offset, offset + length));
  if (bytes.length >= 24 && ascii(1, 3) === 'PNG' && bytes[0] === 137) {
    // PNG chunk lengths let us detect APNG without decoding/flattening frames.
    let offset = 8;
    while (offset + 12 <= bytes.length) {
      const kind = ascii(offset + 4, 4);
      if (kind === 'acTL') return { width: view.getUint32(16), height: view.getUint32(20), animated: true };
      if (kind === 'IDAT') break;
      offset += view.getUint32(offset) + 12;
    }
    return { width: view.getUint32(16), height: view.getUint32(20) };
  }
  if (bytes.length >= 12 && ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') {
    if (bytes.length >= 30 && ascii(12, 4) === 'VP8X') return {
      width: 1 + bytes[24] + (bytes[25] << 8) + (bytes[26] << 16),
      height: 1 + bytes[27] + (bytes[28] << 8) + (bytes[29] << 16), animated: !!(bytes[20] & 2) };
    return null; // Other WebP layouts go to the worker; no speculative decode.
  }
  if (bytes.length >= 4 && bytes[0] === 255 && bytes[1] === 216) {
    let offset = 2; let metadata = false;
    while (offset + 8 < bytes.length) {
      if (bytes[offset++] !== 255) return null;
      while (bytes[offset] === 255) offset++;
      const marker = bytes[offset++];
      if (marker === 0xe1 || marker === 0xe2 || marker === 0xfe) metadata = true;
      if (marker === 0xda || marker === 0xd9) return null;
      if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
      const length = view.getUint16(offset);
      if (length < 2) return null;
      if ([0xc0, 0xc1, 0xc2].includes(marker)) return { width: view.getUint16(offset + 5), height: view.getUint16(offset + 3), metadata };
      offset += length;
    }
  }
  return null;
}
export function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MiB` : `${Math.round(bytes / 1024)} KiB`;
}
export function describeDownscale(result: DownscaleResult): string {
  return `${formatBytes(result.originalBytes)} ÃƒÆ’Ã†’Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†’Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â ÃƒÆ’Ã†’Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¾Ãƒâ€šÃ‚Â¢ ${formatBytes(result.bytes)}`;
}
